import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft, GripVertical, Trash2, Plus, ExternalLink, Copy, ChevronRight,
  Route as RouteIcon, Save, BarChart3, PlayCircle, PauseCircle,
} from "lucide-react";

function SortableStep({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="vp-card p-4" data-testid={TIDS.funnelStepRow}>
      <div className="flex items-center gap-3">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-[#747680] hover:text-[#0B5CFF]" title="Drag to reorder">
          <GripVertical className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

export default function FunnelBuilder() {
  const { id } = useParams();
  const [funnel, setFunnel] = useState(null);
  const [pages, setPages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [pickPageId, setPickPageId] = useState("");
  const [pickLabel, setPickLabel] = useState("Landing");
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const load = useCallback(async () => {
    const [{ data: f }, { data: p }, { data: a }] = await Promise.all([
      api.get(`/funnels/${id}`),
      api.get(`/landing-pages`),
      api.get(`/funnels/${id}/analytics`),
    ]);
    setFunnel(f); setPages(p); setAnalytics(a);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const persistSteps = async (nextSteps) => {
    setSaving(true);
    try {
      const payload = nextSteps.map((s) => ({ page_id: s.page_id, label: s.label }));
      const { data } = await api.patch(`/funnels/${id}`, { steps: payload });
      setFunnel(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = funnel.steps.findIndex((s) => s.step_id === active.id);
    const newIdx = funnel.steps.findIndex((s) => s.step_id === over.id);
    const nextSteps = arrayMove(funnel.steps, oldIdx, newIdx);
    setFunnel({ ...funnel, steps: nextSteps });
    persistSteps(nextSteps);
  };

  const addStep = async () => {
    if (!pickPageId) { toast.error("Choose a landing page"); return; }
    const nextSteps = [...funnel.steps, { page_id: pickPageId, label: pickLabel || "Step" }];
    setShowAdd(false); setPickPageId(""); setPickLabel("Landing");
    await persistSteps(nextSteps);
  };

  const removeStep = async (stepId) => {
    if (!window.confirm("Remove this step from the funnel?")) return;
    const nextSteps = funnel.steps.filter((s) => s.step_id !== stepId);
    await persistSteps(nextSteps);
  };

  const renameStep = async (stepId, label) => {
    const nextSteps = funnel.steps.map((s) => s.step_id === stepId ? { ...s, label } : s);
    setFunnel({ ...funnel, steps: nextSteps });
    await persistSteps(nextSteps);
  };

  const renameFunnel = async () => {
    try {
      const { data } = await api.patch(`/funnels/${id}`, { name: funnel.name });
      setFunnel(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleStatus = async () => {
    const next = funnel.status === "active" ? "draft" : "active";
    if (next === "active" && funnel.steps.length < 2) {
      toast.error("Add at least 2 steps before activating");
      return;
    }
    if (next === "active" && funnel.steps.some((s) => !s.page?.published)) {
      toast.error("All steps must be published landing pages");
      return;
    }
    try {
      const { data } = await api.patch(`/funnels/${id}`, { status: next });
      setFunnel(data);
      toast.success(next === "active" ? "Funnel is live" : "Funnel paused");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/f/${funnel.slug}`);
    toast.success("Funnel link copied");
  };

  if (!funnel) return <div className="text-[#4A4D57]">Loading…</div>;

  const availablePages = pages.filter((p) => p.published);

  return (
    <div className="space-y-6 vp-fade-up">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/app/funnels" className="inline-flex items-center gap-2 text-sm text-[#4A4D57] hover:text-[#0B5CFF]">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All funnels
          </Link>
          <div className="vp-label mt-3">Funnel builder</div>
          <input
            className="mt-1 font-display text-3xl font-bold bg-transparent outline-none border-b-2 border-transparent focus:border-[#0B5CFF] pb-1 w-full max-w-xl"
            value={funnel.name}
            onChange={(e) => setFunnel({ ...funnel, name: e.target.value })}
            onBlur={renameFunnel}
          />
          <div className="text-xs text-[#747680] mt-1">
            Slug · <span className="font-mono">/f/{funnel.slug}</span> · {saving ? "Saving…" : "Saved"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {funnel.status === "active" && (
            <>
              <button onClick={copyLink} data-testid={TIDS.funnelCopyLinkBtn} className="vp-btn-ghost text-sm inline-flex items-center gap-2">
                <Copy className="w-4 h-4" /> Copy link
              </button>
              <a href={`/f/${funnel.slug}`} target="_blank" rel="noreferrer" className="vp-btn-ghost text-sm inline-flex items-center gap-2">
                <ExternalLink className="w-4 h-4" /> Open
              </a>
            </>
          )}
          <button
            onClick={toggleStatus}
            data-testid={TIDS.funnelActivateBtn}
            className={"text-sm inline-flex items-center gap-2 " + (funnel.status === "active" ? "vp-btn-ghost" : "vp-btn-primary")}
          >
            {funnel.status === "active" ? <><PauseCircle className="w-4 h-4" /> Pause</> : <><PlayCircle className="w-4 h-4" /> Activate</>}
          </button>
        </div>
      </div>

      {/* Steps visual chain */}
      {funnel.steps.length > 0 && (
        <div className="vp-card p-5">
          <div className="vp-label mb-3">Flow preview</div>
          <div className="flex items-center flex-wrap gap-2">
            {funnel.steps.map((s, i) => (
              <span key={s.step_id} className="inline-flex items-center gap-2">
                <span className="px-3 py-1.5 rounded-lg text-sm bg-[#0B5CFF]/8 border border-[#0B5CFF]/25 text-[#0B0D17] font-medium">
                  {i + 1}. {s.label}
                </span>
                {i < funnel.steps.length - 1 && <ChevronRight className="w-4 h-4 text-[#747680]" />}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Steps editor */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={funnel.steps.map((s) => s.step_id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {funnel.steps.map((s, i) => {
              const stepStats = analytics?.steps?.find((x) => x.step_id === s.step_id);
              return (
                <SortableStep key={s.step_id} id={s.step_id}>
                  <div className="grid sm:grid-cols-12 gap-3 items-center">
                    <div className="sm:col-span-1 text-2xl font-display font-bold text-[#0B5CFF]">{i + 1}</div>
                    <div className="sm:col-span-4">
                      <div className="vp-label mb-1" style={{ fontSize: "0.65rem" }}>Step label</div>
                      <input
                        className="vp-input text-sm"
                        value={s.label}
                        onChange={(e) => setFunnel({ ...funnel, steps: funnel.steps.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
                        onBlur={(e) => renameStep(s.step_id, e.target.value)}
                      />
                    </div>
                    <div className="sm:col-span-4">
                      <div className="vp-label mb-1" style={{ fontSize: "0.65rem" }}>Landing page</div>
                      <div className="text-sm font-medium">{s.page?.title || <span className="text-[#B00020]">Page missing</span>}</div>
                      <div className="text-xs text-[#747680]">/p/{s.page?.slug || "—"} {s.page?.published === false && <span className="text-[#B00020]">· not published</span>}</div>
                    </div>
                    <div className="sm:col-span-2 text-center">
                      <div className="vp-label mb-1" style={{ fontSize: "0.65rem" }}>Views · Conv.</div>
                      <div className="text-sm font-semibold">{stepStats?.views ?? 0} · {stepStats?.conversions ?? 0}</div>
                      {stepStats?.drop_off_pct_from_prev != null && (
                        <div className="text-xs text-[#B00020]">↓ {stepStats.drop_off_pct_from_prev}%</div>
                      )}
                    </div>
                    <div className="sm:col-span-1 text-right">
                      <button onClick={() => removeStep(s.step_id)} data-testid={TIDS.funnelDeleteStepBtn} className="p-1.5 text-[#4A4D57] hover:text-[#B00020] hover:bg-[#FDE8EA] rounded-md">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </SortableStep>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      <button
        data-testid={TIDS.funnelAddStepBtn}
        onClick={() => setShowAdd(true)}
        className="vp-btn-ghost text-sm inline-flex items-center gap-2"
      >
        <Plus className="w-4 h-4" /> Add step
      </button>

      {/* Analytics footer */}
      {analytics && analytics.steps?.length > 0 && (
        <div className="vp-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-[#0B5CFF]" />
            <div className="vp-label">Funnel analytics</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-[#747680]">Top of funnel views</div>
              <div className="font-display font-bold text-2xl">{analytics.steps[0]?.views ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-[#747680]">Bottom of funnel conversions</div>
              <div className="font-display font-bold text-2xl">{analytics.steps[analytics.steps.length - 1]?.conversions ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-[#747680]">Overall conversion</div>
              <div className="font-display font-bold text-2xl text-[#0B5CFF]">{analytics.overall_conversion_pct}%</div>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5" onClick={() => setShowAdd(false)}>
          <div className="vp-glass p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display font-bold text-xl">Add funnel step</h2>
            <p className="text-sm text-[#4A4D57] mt-1">Pick an existing published landing page and give this step a label.</p>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Step label</span>
                <input className="vp-input text-sm" value={pickLabel} onChange={(e) => setPickLabel(e.target.value)} placeholder="e.g. Sales page" />
              </label>
              <label className="block">
                <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Landing page</span>
                {availablePages.length === 0 ? (
                  <div className="text-sm text-[#B00020]">No published landing pages yet. <Link to="/app/pages" className="underline">Create one →</Link></div>
                ) : (
                  <select className="vp-input text-sm" value={pickPageId} onChange={(e) => setPickPageId(e.target.value)}>
                    <option value="">Select a page…</option>
                    {availablePages.map((p) => (
                      <option key={p.id} value={p.id}>{p.title} · /p/{p.slug}</option>
                    ))}
                  </select>
                )}
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={addStep} disabled={!pickPageId} className="vp-btn-primary text-sm">Add step</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
